import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import PanelTemplate from '../../../templates/Panel';
import PageHeader from '../../../components/PageHeader';
import Box from '../../../components/Box';
import Button from '../../../components/Button';
import { Loading } from '../../../components/Loading';
import { ModalDelete } from '../../../components/ModalDelete';

import useAutomations from './useAutomations';
import RuleCard from './RuleCard';
import RuleEditorModal from './RuleEditorModal';
import TriggerPickerModal from './TriggerPickerModal';
import { TRIGGERS, triggerGroupTitle, triggerOrdersByPriority } from '../../../seeds/crm/automations';

const GROUP_ORDER = [TRIGGERS.BUDGET_CREATED, TRIGGERS.ORDER_CREATED, TRIGGERS.CARD_ENTERED_STAGE];

const sortedStages = (pipeline) =>
    [...(pipeline?.stages ?? [])].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

const SortableRuleCard = ({ rule, index, onEdit, onToggle, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    };
    const dragHandle = (
        <span className="rule-card__drag" {...attributes} {...listeners} aria-label="Reordenar">⋮⋮</span>
    );
    return (
        <div ref={setNodeRef} style={style}>
            <RuleCard rule={rule} index={index} dragHandle={dragHandle} onEdit={onEdit} onToggle={onToggle} onDelete={onDelete} />
        </div>
    );
};

const CrmAutomations = () => {
    const { pipelineId } = useParams();
    const navigate = useNavigate();
    const { automations, pipelines, loading, create, update, toggle, remove, reorder } = useAutomations(pipelineId);

    const [showTriggerPicker, setShowTriggerPicker] = useState(false);
    const [showEditor, setShowEditor] = useState(false);
    const [editorRule, setEditorRule] = useState(null);
    const [editorTrigger, setEditorTrigger] = useState(null);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [applyingRecommended, setApplyingRecommended] = useState(false);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const grouped = useMemo(() => {
        const acc = {};
        GROUP_ORDER.forEach((t) => (acc[t] = []));
        automations.forEach((a) => {
            if (!acc[a.trigger]) acc[a.trigger] = [];
            acc[a.trigger].push(a);
        });
        Object.keys(acc).forEach((t) =>
            acc[t].sort((a, b) => (Number(a.priority) || 0) - (Number(b.priority) || 0))
        );
        return acc;
    }, [automations]);

    const scopedPipeline = pipelineId ? pipelines.find((p) => String(p.id) === String(pipelineId)) : null;
    const firstPipeline = pipelines[0];
    const firstStage = firstPipeline ? sortedStages(firstPipeline)[0] : null;

    const openCreateFor = (trigger) => {
        setEditorRule(null);
        setEditorTrigger(trigger);
        setShowTriggerPicker(false);
        setShowEditor(true);
    };

    const openEdit = (rule) => {
        setEditorRule(rule);
        setEditorTrigger(null);
        setShowEditor(true);
    };

    const handleEditorSubmit = (body) =>
        editorRule ? update(editorRule.id, body) : create(body);

    const handleDelete = () => {
        if (!ruleToDelete) return;
        setDeleting(true);
        Promise.resolve(remove(ruleToDelete.id))
            .then(() => setRuleToDelete(null))
            .finally(() => setDeleting(false));
    };

    const handleDragEnd = (trigger, event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const rules = grouped[trigger];
        const oldIndex = rules.findIndex((r) => r.id === active.id);
        const newIndex = rules.findIndex((r) => r.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        const newRules = arrayMove(rules, oldIndex, newIndex);
        reorder(trigger, newRules.map((r) => r.id));
    };

    const applyRecommended = async () => {
        if (!firstPipeline || !firstStage) return;
        setApplyingRecommended(true);
        try {
            await create({
                trigger: TRIGGERS.BUDGET_CREATED,
                pipeline_id: firstPipeline.id,
                pipeline_stage_id: firstStage.id,
                conditions: null,
                action_config: null,
            });
            await create({
                trigger: TRIGGERS.ORDER_CREATED,
                pipeline_id: firstPipeline.id,
                pipeline_stage_id: firstStage.id,
                conditions: null,
                action_config: {
                    card_creation_mode: 'per_product',
                    card_responsible_mode: 'order_admin',
                    card_responsible_admin_id: null,
                },
            });
        } catch (e) {
            // erros já são exibidos pelo hook
        } finally {
            setApplyingRecommended(false);
        }
    };

    const renderGroup = (trigger) => {
        const rules = grouped[trigger] || [];
        // Oculta grupos vazios quando já existem automações em outros grupos.
        if (rules.length === 0) return null;
        const ordered = triggerOrdersByPriority(trigger);

        return (
            <Box
                key={trigger}
                title={triggerGroupTitle(trigger)}
                tools={<Button type="link" size="small" text="Nova" action={() => openCreateFor(trigger)} />}
            >
                {ordered ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(trigger, e)}>
                        <SortableContext items={rules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                            {rules.map((rule, index) => (
                                <SortableRuleCard
                                    key={rule.id}
                                    rule={rule}
                                    index={index}
                                    onEdit={() => openEdit(rule)}
                                    onToggle={() => toggle(rule.id)}
                                    onDelete={setRuleToDelete}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                ) : (
                    rules.map((rule) => (
                        <RuleCard
                            key={rule.id}
                            rule={rule}
                            onEdit={() => openEdit(rule)}
                            onToggle={() => toggle(rule.id)}
                            onDelete={setRuleToDelete}
                        />
                    ))
                )}

                {ordered && rules.length > 1 && (
                    <p className="automations__hint">
                        As regras são lidas de cima para baixo. A primeira que combinar é a que vale.
                    </p>
                )}
            </Box>
        );
    };

    const automationsContent = (
        <>
            {automations.length === 0 ? (
                <Box>
                    <div className="automations__empty">
                        <span className="automations__empty-icon">📥</span>
                        {firstPipeline ? (
                            <>
                                <h2>Comece com a configuração recomendada</h2>
                                <p>
                                    Vamos criar duas automações para você: todo orçamento e todo pedido viram um card
                                    no seu primeiro painel. Você pode mudar depois.
                                </p>
                                <Button
                                    type="primary"
                                    text="Usar configuração recomendada"
                                    action={applyRecommended}
                                    loading={applyingRecommended}
                                />
                                <Button type="link" text="Prefiro montar do zero" action={() => setShowTriggerPicker(true)} />
                            </>
                        ) : (
                            <>
                                <h2>Crie seu primeiro painel</h2>
                                <p>Você precisa de um painel com colunas antes de configurar automações.</p>
                                <Button type="primary" text="Criar meu primeiro painel" link="/crm/painel/novo" />
                            </>
                        )}
                    </div>
                </Box>
            ) : (
                GROUP_ORDER.map((trigger) => renderGroup(trigger))
            )}
        </>
    );

    return (
        <PanelTemplate id="crm-automations">
            <PageHeader
                left={
                    <div>
                        <h1>{scopedPipeline ? `Automações · ${scopedPipeline.name}` : 'Automações'}</h1>
                        <p className="automations__subtitle">Configure o que acontece sozinho nos seus painéis.</p>
                        {scopedPipeline && (
                            <Button type="link" size="small" text="Ver todas as automações" action={() => navigate('/crm/automacoes')} />
                        )}
                    </div>
                }
                right={
                    <Button type="primary" size="small" svg="plus-white" text="Nova automação" action={() => setShowTriggerPicker(true)} />
                }
            />

            {loading ? (
                <Loading show={true} />
            ) : (
                automationsContent
            )}

            <TriggerPickerModal
                show={showTriggerPicker}
                close={() => setShowTriggerPicker(false)}
                onPick={openCreateFor}
            />

            <RuleEditorModal
                show={showEditor}
                close={() => setShowEditor(false)}
                onSubmit={handleEditorSubmit}
                rule={editorRule}
                initialTrigger={editorTrigger}
                pipelines={pipelines}
            />

            <ModalDelete
                show={ruleToDelete !== null}
                close={() => setRuleToDelete(null)}
                action={handleDelete}
                loading={deleting}
                text="Tem certeza que deseja excluir esta automação?"
            />
        </PanelTemplate>
    );
};

export default CrmAutomations;
